import { useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as yup from "yup";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../constants";
import { useTransshipmentState } from "../context/TransshipmentContext";
import { Transshipment } from "../models/transshipment";
import TransshipmentService from "../services/transshipment.service";
import { TaskStatus, TransshipmentWhereToLoad } from "../util/enums";
import useActionAutomaticSequence from "./useActionAutomaticSequence";

const PAGE_SIZE = 10;

export const useActionTransshipment = () => {
  const { transshipment, sequence, setErrors, setOrders, setCountOrders } = useTransshipmentState();
  const { increaseAutomaticSequence, isAutomaticSequenceReference } = useActionAutomaticSequence();
  const { t: translate } = useTranslation();

  const [isSubmitted, setIsSubmitted] = useState(false);

  const history = useHistory();

  const sendTransshipment = async (publish?: boolean) => {
    if (transshipment) {
      setIsSubmitted(true);
      if (handleValidate()) {
        const status = publish ? TaskStatus.PUBLISHED : transshipment?.status;

        if (transshipment?.id) {
          updateTransshipment({ transshipment: { ...transshipment, status } });
        } else {
          createTransshipment({ transshipment: { ...transshipment, status } });
        }
      } else {
        NotificationToast.error(translate("pleaseFillInTheRequiredFieldsBeforeSaving"), {
          id: "transshipmentRequiredFields",
        });
      }
    }
  };

  const handleValidate = () => {
    try {
      const schema = yup.object().shape({
        type: yup
          .string()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        dueBy: yup
          .string()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        status: yup
          .string()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        warehouseId: yup
          .string()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        createdById: yup
          .number()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        whatToDo: yup
          .string()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        hasStorageBoxSingleUse: yup.boolean(),
        jobs: yup.array().when("hasStorageBoxSingleUse", {
          is: true,
          then: yup
            .array()
            .length(1, translate("youHaveToAddAtLeastOneOrder"))
            .required(translate("thisFieldIsRequired"))
            .typeError(translate("thisFieldIsRequired")),
          otherwise: yup
            .array()
            .min(1, translate("youHaveToAddAtLeastOneOrder"))
            .required(translate("thisFieldIsRequired"))
            .typeError(translate("thisFieldIsRequired")),
        }),
      });

      schema.validateSync(
        {
          type: transshipment?.typeId,
          dueBy: transshipment?.dueBy,
          status: transshipment?.status,
          whatToDo: transshipment?.whatToDo,
          warehouseId: transshipment?.warehouseId,
          createdById: transshipment?.createdById,
          hasStorageBoxSingleUse:
            transshipment?.whereLoad === TransshipmentWhereToLoad.STORAGE_BOX_SINGLE_USE,
          jobs: transshipment?.jobs,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  useEffect(() => {
    if (isSubmitted) {
      handleValidate();
    }
  }, [transshipment]);

  const onSuccess = () => {
    if (
      sequence &&
      transshipment?.reference &&
      sequence?.id &&
      isAutomaticSequenceReference(sequence, transshipment?.reference)
    )
      increaseAutomaticSequence({
        sequence,
        value: transshipment?.reference,
      });

    history.push(PRIVATE_ROUTES.TRANSSHIPMENTS);
  };

  const { mutate: searchOrders, isLoading: isSearching } = useMutation(
    ["transshipment.getOrders"],
    ({
      warehouseId,
      type,
      reference,
      page,
    }: {
      warehouseId?: number;
      type?: string;
      reference?: string;
      page?: number;
    }) =>
      TransshipmentService.getOrders({
        type: type?.replace(/ /g, ""),
        reference,
        page,
        pageSize: PAGE_SIZE,
        warehouseId,
      }),
    {
      onSuccess: (response) => {
        if (response?.data) {
          setOrders(response?.data?.rows);
          setCountOrders(response?.data?.count);
        }
      },
    }
  );

  const { mutate: createTransshipment, isLoading: isCreating } = useMutation(
    ["transshipment.create"],
    ({ transshipment }: { transshipment: Transshipment }) =>
      TransshipmentService.create(transshipment),
    { onSuccess }
  );

  const { mutate: updateTransshipment, isLoading: isUpdating } = useMutation(
    ["transshipment.update"],
    ({ transshipment }: { transshipment: Transshipment }) =>
      TransshipmentService.update(Number(transshipment?.id), transshipment),
    { onSuccess }
  );

  return {
    sendTransshipment,
    isLoading: isCreating || isUpdating,
    searchOrders,
    isSearching,
    pageSize: PAGE_SIZE,
  };
};
